// Country-pack authoring editor (Platform Ops Console, Phase 3). A governed drawer to
// create/edit a DRAFT statutory pack, validate + dry-run preview it, upload the tax
// sign-off, and drive the lifecycle (submit → publish/reject). Publishing is gated in
// the backend on maker-checker + required sign-off + future effective date; this UI
// surfaces those and the backend's messages. See docs/country-pack-authoring.md.
import React, { useEffect, useState } from 'react';
import {
  Drawer, Button, Space, Input, InputNumber, Select, Switch, DatePicker, Table, Tag,
  Typography, Alert, Divider, message, Upload, Popconfirm, Descriptions,
} from 'antd';
import { PlusOutlined, DeleteOutlined, UploadOutlined, CheckCircleOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import {
  getCountryPack, createCountryPackDraft, updateCountryPackDraft, submitCountryPack,
  publishCountryPack, rejectCountryPack, discardCountryPack, validateCountryPack,
  previewCountryPack, uploadCountryPackSignoff,
  type DraftRule, type PackValidation, type PackPreview, type PackRuleConfig, type PackBand,
} from '../../services/countryPackService';

const { Text, Title } = Typography;

// Surface the backend's message (admin-portal has no shared apiError helper).
const errMsg = (e: any, fallback: string): string => e?.response?.data?.message || fallback;

type Mode = { kind: 'create'; country: string; cloneFromId?: string } | { kind: 'edit'; id: string };

interface Props {
  mode: Mode | null;
  canPublish: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const emptyRule = (order: number): DraftRule => ({
  code: '', kind: 'percent', order_index: order, payer: 'employee', pre_tax: false, config: {},
});

export const CountryPackEditor: React.FC<Props> = ({ mode, canPublish, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [id, setId] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('draft');
  const [name, setName] = useState('');
  const [rules, setRules] = useState<DraftRule[]>([]);
  const [effectiveFrom, setEffectiveFrom] = useState<Dayjs | null>(null);
  const [validation, setValidation] = useState<PackValidation | null>(null);
  const [preview, setPreview] = useState<PackPreview | null>(null);

  useEffect(() => {
    if (!mode) return;
    setValidation(null); setPreview(null); setEffectiveFrom(null);
    void (async () => {
      setLoading(true);
      try {
        if (mode.kind === 'edit') {
          const d = await getCountryPack(mode.id);
          setId(d.id); setStatus(d.status ?? 'draft'); setName(d.name);
          setRules(d.rules.map((r) => ({
            code: r.code, kind: r.kind as DraftRule['kind'], order_index: r.order_index,
            payer: r.payer as DraftRule['payer'], pre_tax: r.pre_tax, config: r.config ?? {},
          })));
        } else {
          setId(null); setStatus('draft'); setName(`${mode.country} pack`);
          if (mode.cloneFromId) {
            const src = await getCountryPack(mode.cloneFromId);
            setName(src.name);
            setRules(src.rules.map((r) => ({
              code: r.code, kind: r.kind as DraftRule['kind'], order_index: r.order_index,
              payer: r.payer as DraftRule['payer'], pre_tax: r.pre_tax, config: r.config ?? {},
            })));
          } else {
            setRules([emptyRule(1)]);
          }
        }
      } catch (e) {
        message.error(errMsg(e, 'Could not load pack'));
      } finally {
        setLoading(false);
      }
    })();
  }, [mode]);

  if (!mode) return null;
  const editable = status === 'draft';
  const country = mode.kind === 'create' ? mode.country : '';

  const setRule = (i: number, patch: Partial<DraftRule>) =>
    setRules((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const setConfig = (i: number, patch: Partial<PackRuleConfig>) =>
    setRules((rs) => rs.map((r, j) => (j === i ? { ...r, config: { ...r.config, ...patch } } : r)));

  // Persist the draft (create on first save, then update). Returns the id.
  const saveDraft = async (): Promise<string | null> => {
    setSaving(true);
    try {
      const ruled = rules.map((r, i) => ({ ...r, order_index: i + 1 }));
      if (!id) {
        const created = await createCountryPackDraft({
          country, name, rules: ruled, clone_from_id: mode.kind === 'create' ? mode.cloneFromId : undefined,
        });
        setId(created.id);
        message.success('Draft created');
        return created.id;
      }
      await updateCountryPackDraft(id, { name, rules: ruled });
      message.success('Draft saved');
      return id;
    } catch (e) {
      message.error(errMsg(e, 'Could not save draft'));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const runValidate = async () => {
    const pid = (await saveDraft());
    if (!pid) return;
    try { setValidation(await validateCountryPack(pid)); }
    catch (e) { message.error(errMsg(e, 'Validation failed')); }
  };
  const runPreview = async () => {
    const pid = (await saveDraft());
    if (!pid) return;
    try { setPreview(await previewCountryPack(pid)); }
    catch (e) { message.error(errMsg(e, 'Preview failed')); }
  };
  const doSubmit = async () => {
    const pid = (await saveDraft());
    if (!pid) return;
    try { await submitCountryPack(pid); message.success('Submitted for review'); onSaved(); }
    catch (e) { message.error(errMsg(e, 'Could not submit')); }
  };
  const doPublish = async () => {
    if (!id) return;
    if (!effectiveFrom) { message.error('Pick a future effective date'); return; }
    try {
      await publishCountryPack(id, effectiveFrom.format('YYYY-MM-DD'));
      message.success('Published'); onSaved();
    } catch (e) { message.error(errMsg(e, 'Could not publish')); }
  };
  const doReject = async () => {
    if (!id) return;
    try { await rejectCountryPack(id); message.success('Sent back to draft'); onSaved(); }
    catch (e) { message.error(errMsg(e, 'Could not reject')); }
  };
  const doDiscard = async () => {
    if (!id) return;
    try { await discardCountryPack(id); message.success('Draft discarded'); onSaved(); }
    catch (e) { message.error(errMsg(e, 'Could not discard')); }
  };
  const doUpload = async (file: File) => {
    const pid = (await saveDraft());
    if (!pid) return false;
    try { await uploadCountryPackSignoff(pid, file, {}); message.success('Sign-off attached'); }
    catch (e) { message.error(errMsg(e, 'Upload failed')); }
    return false; // prevent antd's default upload
  };

  return (
    <Drawer
      title={mode.kind === 'create' ? `New ${country} pack version` : `Edit pack · ${status}`}
      width={860}
      open={!!mode}
      onClose={onClose}
      loading={loading}
      extra={
        <Space>
          {editable && <Button onClick={() => void saveDraft()} loading={saving}>Save draft</Button>}
          {editable && <Button onClick={() => void runValidate()}>Validate</Button>}
          {editable && <Button onClick={() => void runPreview()}>Preview</Button>}
        </Space>
      }
    >
      {!editable && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message={`This pack is ${status} and can no longer be edited.`}
          description={status === 'in_review' ? 'A second operator (publisher) approves or rejects it.' : undefined}
        />
      )}

      <Descriptions size="small" column={2} style={{ marginBottom: 12 }}>
        <Descriptions.Item label="Name">
          {editable ? <Input value={name} onChange={(e) => setName(e.target.value)} /> : name}
        </Descriptions.Item>
        <Descriptions.Item label="Status"><Tag>{status}</Tag></Descriptions.Item>
      </Descriptions>

      <Title level={5}>Rules</Title>
      {rules.map((r, i) => (
        <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 10 }}>
          <Space wrap align="center">
            <Input placeholder="code (paye)" style={{ width: 120 }} disabled={!editable}
              value={r.code} onChange={(e) => setRule(i, { code: e.target.value })} />
            <Select style={{ width: 120 }} disabled={!editable} value={r.kind}
              onChange={(v) => setRule(i, { kind: v })}
              options={['bracket', 'percent', 'fixed', 'formula'].map((k) => ({ value: k, label: k }))} />
            <Select style={{ width: 130 }} disabled={!editable} value={r.payer}
              onChange={(v) => setRule(i, { payer: v })}
              options={[{ value: 'employee', label: 'employee' }, { value: 'employer', label: 'employer' }]} />
            <span><Text type="secondary">pre-tax </Text>
              <Switch size="small" disabled={!editable} checked={r.pre_tax} onChange={(v) => setRule(i, { pre_tax: v })} /></span>
            {editable && (
              <Button danger size="small" icon={<DeleteOutlined />}
                onClick={() => setRules((rs) => rs.filter((_, j) => j !== i))} />
            )}
          </Space>
          <div style={{ marginTop: 8 }}>
            {r.kind === 'percent' && (
              <Space>Rate (fraction)
                <InputNumber min={0} max={1} step={0.01} disabled={!editable}
                  value={r.config.rate ? Number(r.config.rate) : undefined}
                  onChange={(v) => setConfig(i, { rate: v == null ? undefined : String(v) })} />
              </Space>
            )}
            {r.kind === 'fixed' && (
              <Space>Amount
                <InputNumber min={0} disabled={!editable}
                  value={r.config.amount ? Number(r.config.amount) : undefined}
                  onChange={(v) => setConfig(i, { amount: v == null ? undefined : String(v) })} />
              </Space>
            )}
            {r.kind === 'formula' && (
              <Input placeholder="expression" disabled={!editable}
                value={r.config.when || ''} onChange={(e) => setConfig(i, { when: e.target.value })} />
            )}
            {r.kind === 'bracket' && (
              <BandEditor editable={editable} bands={r.config.bands ?? []}
                onChange={(bands) => setConfig(i, { bands })} />
            )}
          </div>
        </div>
      ))}
      {editable && (
        <Button icon={<PlusOutlined />} onClick={() => setRules((rs) => [...rs, emptyRule(rs.length + 1)])}>
          Add rule
        </Button>
      )}

      {validation && (
        <>
          <Divider />
          <Title level={5}>Validation</Title>
          {validation.errors.length === 0 && validation.warnings.length === 0 && (
            <Alert type="success" showIcon message="No issues" />
          )}
          {validation.errors.map((e, i) => <Alert key={`e${i}`} type="error" showIcon message={e} style={{ marginBottom: 6 }} />)}
          {validation.warnings.map((w, i) => <Alert key={`w${i}`} type="warning" showIcon message={w} style={{ marginBottom: 6 }} />)}
        </>
      )}

      {preview && (
        <>
          <Divider />
          <Title level={5}>Preview · draft vs current {preview.has_current ? '' : '(no current pack)'}</Title>
          <Table
            size="small" pagination={false} rowKey="gross"
            dataSource={preview.samples}
            columns={[
              { title: `Gross (${preview.currency})`, dataIndex: 'gross', render: (g: string) => Number(g).toLocaleString() },
              { title: 'Draft net', render: (_: unknown, s) => (s.draft ? Number(s.draft.net).toLocaleString() : '—') },
              { title: 'Current net', render: (_: unknown, s) => (s.current ? Number(s.current.net).toLocaleString() : '—') },
              {
                title: 'Δ net', render: (_: unknown, s) => {
                  if (!s.draft || !s.current) return '—';
                  const d = Number(s.draft.net) - Number(s.current.net);
                  return <Text type={d === 0 ? undefined : d > 0 ? 'success' : 'danger'}>{d > 0 ? '+' : ''}{d.toLocaleString()}</Text>;
                },
              },
            ]}
          />
        </>
      )}

      <Divider />
      <Space direction="vertical" style={{ width: '100%' }}>
        <Space wrap>
          <Upload beforeUpload={(f) => doUpload(f as File)} showUploadList={false} accept=".pdf,.png,.jpg,.jpeg">
            <Button icon={<UploadOutlined />} disabled={status !== 'draft' && status !== 'in_review'}>Upload sign-off</Button>
          </Upload>
          {status === 'draft' && (
            <>
              <Button type="primary" onClick={() => void doSubmit()}>Submit for review</Button>
              <Popconfirm title="Discard this draft?" onConfirm={() => void doDiscard()}>
                <Button danger>Discard</Button>
              </Popconfirm>
            </>
          )}
          {status === 'in_review' && canPublish && (
            <>
              <DatePicker placeholder="Effective from (future)" value={effectiveFrom} onChange={setEffectiveFrom} />
              <Popconfirm
                title="Publish this statutory pack?"
                description="This activates the new version for every tenant in this country on the effective date."
                onConfirm={() => void doPublish()}
              >
                <Button type="primary" icon={<CheckCircleOutlined />} disabled={!effectiveFrom}>Publish</Button>
              </Popconfirm>
              <Button onClick={() => void doReject()}>Send back</Button>
            </>
          )}
          {status === 'in_review' && !canPublish && (
            <Text type="secondary">Awaiting a publisher (platform admin) to approve.</Text>
          )}
        </Space>
      </Space>
    </Drawer>
  );
};

// BandEditor edits progressive bracket bands (from / to / rate).
const BandEditor: React.FC<{ editable: boolean; bands: PackBand[]; onChange: (b: PackBand[]) => void }> = ({ editable, bands, onChange }) => {
  const set = (i: number, patch: Partial<PackBand>) => onChange(bands.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  return (
    <div>
      <Table
        size="small" pagination={false} rowKey={(_, i) => String(i)}
        dataSource={bands}
        columns={[
          { title: 'From', render: (_: unknown, b, i) => <InputNumber disabled={!editable} value={b.from ? Number(b.from) : 0} onChange={(v) => set(i, { from: String(v ?? 0) })} /> },
          { title: 'To (blank = ∞)', render: (_: unknown, b, i) => <InputNumber disabled={!editable} value={b.to ? Number(b.to) : undefined} onChange={(v) => set(i, { to: v == null ? null : String(v) })} /> },
          { title: 'Rate (fraction)', render: (_: unknown, b, i) => <InputNumber min={0} max={1} step={0.01} disabled={!editable} value={b.rate ? Number(b.rate) : 0} onChange={(v) => set(i, { rate: String(v ?? 0) })} /> },
          ...(editable ? [{ title: '', render: (_: unknown, _b: PackBand, i: number) => <Button danger size="small" icon={<DeleteOutlined />} onClick={() => onChange(bands.filter((_, j) => j !== i))} /> }] : []),
        ]}
      />
      {editable && (
        <Button size="small" icon={<PlusOutlined />} style={{ marginTop: 6 }}
          onClick={() => onChange([...bands, { from: '0', to: null, rate: '0' }])}>Add band</Button>
      )}
    </div>
  );
};

export default CountryPackEditor;
